import { BookingDialogActionType } from "../Actions";
import { BookingDialogStep, Direction, IBundle, IRoute, ISearchJourneyResult, IServiceType, ITemporaryTableBooking, ITravelDetailsState } from "../Models";
import { SearchActionType } from "../Search/Actions";


export const createTravelDetailsReducer = (initialState: ITravelDetailsState = null) => {

	initialState = setInitialState(initialState);

	return (state: ITravelDetailsState = initialState, action: { type?: SearchActionType|BookingDialogActionType }): ITravelDetailsState => {
		switch(action.type) {
			case "searchJourneyComplete":
				return searchJourneyComplete(state, (action as any).payload);
			case "selectRoute":
				return selectRoute(state, action as any);
			case "selectBundle":
				return selectBundle(state, action as any);
			case "setOptions":
				return setOptions(state, action as any);
			case "loadServiceInfoComplete":
				return setServiceInfo(state, action as any);
			case "loadAvailableTimesStarted":
				return clearAvailableTimes(state, action as any);
			case "loadAvailableTimesComplete":
				return addAvailableTimes(state, action as any);
			case "clearTemporaryTableStarted":
				return removeTemporaryBooking(state, (action as any).meta.bookingId);
			case "bookTemporaryTableComplete":
				return addTemporaryBooking(state, action as any);
			case "setStep":
				const step = (action as any).step as BookingDialogStep;
				if (step === "search")
					return { ...state, ...{ outbound: undefined, inbound: undefined }}; //Töm state om vi går tillbaka till search
				else
					return state;
			default:
				return state;
		}
	};

	function setInitialState(initialState: ITravelDetailsState): ITravelDetailsState {
		return {...initialState, ...{ }};
	}
};

function searchJourneyComplete(state: ITravelDetailsState, payload: ISearchJourneyResult): ITravelDetailsState {
	if (!(payload?.offer?.travels?.length > 0))
		return state;

	const outbound = payload.offer.travels.find(t => t.direction === "outbound");
	const outboundRoutes = outbound?.routes.filter(x => x.lowestPrice && x.bundleAvailability.availability);
	const inbound = payload.offer.travels.find(t => t.direction === "inbound");
	const inboundRoutes = inbound?.routes.filter(x => x.lowestPrice && x.bundleAvailability.availability);

	let newState = { ...state };
	if (outboundRoutes?.length === 1)
		newState = {...newState,...{ outbound: { route: outboundRoutes[0]} }  };
	if (inboundRoutes?.length === 1)
		newState = {...newState,...{ inbound: { route: inboundRoutes[0]} } };

	return newState;

}

function selectRoute(state: ITravelDetailsState, action: { direction: Direction, route: IRoute }) : ITravelDetailsState
{
	const travelState = { route: action.route };

	return action.direction === "outbound" ?
		{ ...state, ...{ outbound: travelState }} :
		{ ...state, ...{ inbound: travelState }};
}

function selectBundle(state: ITravelDetailsState, action: { direction: Direction, bundle: IBundle }) : ITravelDetailsState
{
	const travelState = {...state[action.direction], ...{ bundle: action.bundle }};

	return action.direction === "outbound" ?
		{ ...state, ...{ outbound: travelState }} :
		{ ...state, ...{ inbound: travelState }};
}

function setOptions(state: ITravelDetailsState, action: { direction: Direction, tariffCode: string, count: number, legId: string }) : ITravelDetailsState
{
	let travelState = action.direction === "outbound" ? state.outbound : state.inbound;

	let options = { ...travelState.options, ...{} };
	let legOptions = { ...options[action.legId], ...{} };

	//Om count är 0 ska vi inte sätta antalet till 0, utan ta bort den
	if (action.count === 0)
	{
		if (!options[action.legId] || !options[action.legId][action.tariffCode]) //Om vi inte hade några options på detta leg, eller på denna tariffCode behöver vi inte ändra något
			return state;

		//Ta bort tariffkoden på legOptions
		delete legOptions[action.tariffCode];

		//Om det inte finns några options kvar på detta leg, ta bort även det
		if (Object.getOwnPropertyNames(legOptions).length === 0)
			delete options[action.legId];
		else
			options = { ...options, ...{ [action.legId]: legOptions } };
	}
	else
	{
		legOptions = { ...legOptions, ...{  [action.tariffCode]: action.count } };
		options = { ...options, ...{ [action.legId]: legOptions } };
	}

	travelState = {...travelState,...{ options }};

	return action.direction === "outbound" ?
		{ ...state, ...{ outbound: travelState }} :
		{ ...state, ...{ inbound: travelState }};
}


interface IServiceInfoResponse {
	name: string;
	serviceIdentifier: string;
	serviceType: IServiceType;
	serviceProperties: {code: string; description: string;}[],
	travelInfo: {title: string; content: string;}[]
}
function setServiceInfo(state: ITravelDetailsState, action: { meta: { direction: Direction, routeId: string }, payload: { items: IServiceInfoResponse[] }}) : ITravelDetailsState
{
	if (!state[action.meta.direction] || state[action.meta.direction].route?.id !== action.meta.routeId )
		return state;

	let travelState = {...state[action.meta.direction]};
	let route = {...travelState.route};
	let legs = [...route.legs];
	action.payload.items.forEach(si => {
		let legIdx = legs.findIndex(x => x.serviceName === si.name);
		if (legIdx < 0)
			return;

		legs[legIdx] = {...legs[legIdx], ...{ serviceInformation: { name: si.name, serviceProperties: si.serviceProperties, travelInfo: si.travelInfo } } };
	});

	route = {...route, ...{ legs }};
	travelState = {...travelState, ...{ route }};
	return action.meta.direction === "outbound" ?
		{ ...state, ...{ outbound: travelState }} :
		{ ...state, ...{ inbound: travelState }};
}

function clearAvailableTimes(state: ITravelDetailsState, action: { meta: { legId: string, productCode: string }})
{
	//Om vi inte hade något tidigare kan vi bara skicka tillbaka state.
	if (!state.tableBookings || !state.tableBookings.availableTimes ||
			!state.tableBookings.availableTimes[action.meta.legId] || !state.tableBookings.availableTimes[action.meta.legId].some(x => x.productCode === action.meta.productCode))
		return state;


	const availableTimes = {...state.tableBookings.availableTimes};
	const availableTimesForLeg = [...state.tableBookings.availableTimes[action.meta.legId]];

	//delete availableTimesForLeg[action.meta.productCode];
	const index = availableTimesForLeg.findIndex(x => x.productCode === action.meta.productCode);
	availableTimesForLeg.splice(index, 1);

	availableTimes[action.meta.legId] = availableTimesForLeg;

	return {...state, ...{ tableBookings: { ...state.tableBookings, ...{ availableTimes} 	}}};
}

function addAvailableTimes(state: ITravelDetailsState, action: { meta: { legId: string, productCode: string }, payload: { items: string[], date: string }})
{
	const tableBookings = {...state.tableBookings }
	const availableTimes = {...tableBookings.availableTimes};
	const availableTimesForLeg = [...(availableTimes[action.meta.legId] || [])];

	// availableTimesForLeg[action.meta.productCode] = action.payload.items;

	const index = availableTimesForLeg.findIndex(x => x.productCode === action.meta.productCode);
	const obj = { productCode: action.meta.productCode, times: action.payload.items, date: action.payload.date };
	if (index >= 0)
		availableTimesForLeg[index] = obj;
	else
		availableTimesForLeg.push(obj)

	availableTimes[action.meta.legId] = availableTimesForLeg;

	return {...state, ...{ tableBookings: { ...state.tableBookings, ...{ availableTimes} 	}}};
}

function removeTemporaryBooking(state: ITravelDetailsState, bookingId: number): ITravelDetailsState {
	if (!state.tableBookings?.bookings?.some(x => x.bookingId === bookingId))
		return state;

	const bookings = [...state.tableBookings.bookings];
	bookings.splice(bookings.findIndex(x => x.bookingId === bookingId), 1);

	return { ...state, ...{ tableBookings: { ...state.tableBookings, ...{ bookings }}}};
}
function addTemporaryBooking(state: ITravelDetailsState, action: { meta: ITemporaryTableBooking, payload: { bookingId: number }}): ITravelDetailsState {
	//Kolla att vi faktiskt har detta leg i outbound eller inbound
	if (!state.outbound?.route?.legs?.some(x => x.id === action.meta.legId && x.serviceName === action.meta.serviceName) &&
		!state.inbound?.route?.legs?.some(x => x.id === action.meta.legId && x.serviceName === action.meta.serviceName))
		return state;

	const direction = state.outbound?.route?.legs.some(leg => leg.id === action.meta.legId) ? "outbound" : "inbound";

	const tableBookings = {...state.tableBookings };
	const bookings = tableBookings.bookings ? [...tableBookings.bookings] : [];
	const booking = {...action.meta, ...{ bookingId: action.payload.bookingId, direction: direction as Direction }};
	bookings.push(booking);

	return { ...state, ...{ tableBookings: { ...tableBookings, ...{ bookings }}}};
}

